import {
  Component,
  DEFAULT_CURRENCY_CODE,
  LOCALE_ID,
  inject,
  input,
} from '@angular/core';
import {
  CurrencyCode,
  LocaleId,
  TranslateService,
  formatCurrency,
  provideTranslationDictionaryMapping,
} from '@i18n';
import { translation } from '../../i18n/translation.constant';
import { TitlePriceVariant } from '../../models/promotion/title-price-variant.enum';
import { BasketService } from '../../services/basket.service';

@Component({
  selector: 'app-single-plan-promo-price',
  standalone: true,
  templateUrl: './single-plan-promo-price.component.html',
  styleUrls: ['./single-plan-promo-price.component.scss'],
  providers: [provideTranslationDictionaryMapping(translation)],
})
export class SinglePlanPromoPriceComponent {
  private readonly basketService = inject(BasketService);
  private readonly translateService = inject(TranslateService);
  private readonly currencyCode = inject<CurrencyCode>(DEFAULT_CURRENCY_CODE);
  private readonly localeId = inject<LocaleId>(LOCALE_ID);
  readonly titlePriceVariant = input<TitlePriceVariant>(
    TitlePriceVariant.BodyRegular,
  );
  readonly verboseSuffix = input<boolean>(false);
  protected readonly TitlePriceVariant = TitlePriceVariant;

  readonly hasDiscountedMonths = () =>
    this.basketService.basketItemQuoteHasDiscountedMonths();
  readonly discountedMonths = () =>
    this.basketService.basketItemQuoteDiscountedMonths();
  readonly discountedMonthlyPayment = () =>
    this.basketService.basketItemQuoteDiscountedMonthlyPayment();
  readonly fullMonthlyPayment = () =>
    this.basketService.basketItemQuoteFullMonthlyPayment();
  readonly quoteMonthlyPayment = () =>
    this.basketService.basketItemQuoteDetailsPaymentOption().subsequentPayment;
  readonly hasPromo = () => this.basketService.basketItemQuoteHasPromo();
  readonly hasFreeMonths = () =>
    this.basketService.basketItemQuoteHasFreeMonths();

  promoLine(): string {
    if (this.hasDiscountedMonths()) {
      const discountedPrice = formatCurrency(
        this.discountedMonthlyPayment(),
        this.currencyCode,
        this.localeId,
      );
      const months = this.discountedMonths();

      return this.translateService.translate(
        'SINGLE_PLAN_SALE_PAGE_FOOTER_PROMO_DISCOUNTED_MONTHS_LINE',
        {
          discountedPrice,
          months,
        },
      );
    }
    if (this.hasFreeMonths()) {
      const freeMonths = this.basketService.basketItemQuoteFreeMonths();
      return this.translateService.translate(
        'SINGLE_PLAN_SALE_PAGE_FOOTER_PROMO_MONTHS_FREE_LINE',
        {
          freeMonths,
        },
      );
    }
    return '';
  }
  suffixLine(): string {
    if (!this.hasPromo()) {
      return '';
    }

    let fullPrice = '';

    if (this.hasDiscountedMonths()) {
      fullPrice = formatCurrency(
        this.fullMonthlyPayment(),
        this.currencyCode,
        this.localeId,
      );
    }
    if (this.hasFreeMonths()) {
      fullPrice = formatCurrency(
        this.quoteMonthlyPayment(),
        this.currencyCode,
        this.localeId,
      );
    }
    return fullPrice
      ? this.translateService.translate(
          this.verboseSuffix()
            ? 'SINGLE_PLAN_SALE_PAGE_FOOTER_PROMO_FULL_PRICE_VERBOSE'
            : 'SINGLE_PLAN_SALE_PAGE_FOOTER_PROMO_FULL_PRICE',
          {
            fullPrice,
          },
        )
      : '';
  }
}
